/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import withBrand from "@/components/with-brand";
import { BRAND_AVANTI, BRAND_STAYSURE, COMPREHENSIVE } from "@/constants";
import {
  BenefitCopyDataType,
  CancellationCovers,
} from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { DynamicCoverLimitProduct } from "@/types/DynamicCoverLimitProductType";
import { quoteShouldHideField } from "@/utils/quote-brand-impact.utlls";

import {
  BasicCell,
  ComparisionTableCellProps,
  ComprehensiveCell,
  MobileComparisonCell,
  SignatureCell,
  TwoColumnLeftCell,
  TwoColumnRightCell,
} from "./comparison-table-cell";

type ComparisonTableDataItems = {
  data: ComparisonTableData; // Specify the type for the `data` prop
  isMobileOnly?: boolean;
  setCancellationCoverOpen?: (val: boolean) => void;
  setCancelltionChangeCoverType?: (val: string) => void;
  setDynamicCoverLimit?: (val: CancellationCovers[] | null) => void;
  brand?: "staysure" | "avanti";
};

type CoverType =
  | "basic"
  | "comprehensive"
  | "signature"
  | "essentials"
  | "classic"
  | "deluxe";
type TwoColumnCoverType = "classic" | "deluxe" | "basic" | "comprehensive";

type ComparisonTableData = {
  [key: string]: {
    title: string;
    comparisonTableCell: {
      [cellKey: string]: {
        text: string;
        icon: "included" | "notIncluded" | "extra" | "none";
        coverType:
          | "basic"
          | "comprehensive"
          | "signature"
          | "essentials"
          | "classic"
          | "deluxe";
      };
    };
    isExcludeUK?: boolean;
  };
};

function ComparisionTableBlock({
  data,
  isMobileOnly,
  setCancellationCoverOpen,
  setCancelltionChangeCoverType,
  setDynamicCoverLimit,
  brand,
}: Readonly<ComparisonTableDataItems>) {
  const { attributes } = useSelector((state: RootState) => state.quote);

  const { current } = useWizard();

  const formatToK = (value: string): string => {
    const number = parseFloat(value.replace(/[^0-9.]/g, "")); // strip non-numeric characters

    if (Number.isNaN(number)) return value;

    return number >= 1000
      ? `${(number / 1000).toFixed(0)}K`
      : number.toString();
  };

  const renderTableBlocks = (dataItems: ComparisonTableData) => {
    return Object.keys(dataItems).map((rowKey) => {
      if (dataItems[rowKey]?.title === undefined) return null;
      if (
        dataItems[rowKey].isExcludeUK &&
        quoteShouldHideField(attributes?.tripDetails)
      ) {
        return null;
      }
      const { title, comparisonTableCell } = dataItems[rowKey];
      const hasIcon = () => {
        return Object.values(comparisonTableCell).some(
          (cell) => cell.icon !== "none"
        );
      };

      const classes = ["flex-row", "flex"];

      const cellCount = Object.keys(comparisonTableCell).length;

      if (cellCount === 2) {
        const justifySelf = isMobileOnly
          ? "justify-self-auto"
          : "justify-self-center";
        classes.push(justifySelf, "items-end");
      }

      if (isMobileOnly) {
        classes.push("gap-spacing-l");
      }

      const className = classes.join(" ");

      return (
        <div
          key={rowKey}
          className={`grid  ${
            isMobileOnly
              ? "w-full px-spacing-s pt-spacing-m"
              : "pt-[16px] md:w-[390px] sm:w-[312px]"
          } relative bottom-[10px] justify-self-center ${
            hasIcon() ? "sm:h-[110px]" : "sm:h-[88px]"
          } ${isMobileOnly ? "sm:h-auto" : ""}`}
        >
          <span
            className={`${
              isMobileOnly ? "" : "text-center justify-self-center"
            } ${
              isMobileOnly && brand === BRAND_AVANTI
                ? "!leading-[21px] !text-[18px] !text-surface-neutral-option-9"
                : ""
            } ${isMobileOnly && brand !== BRAND_AVANTI ? "!text-[20px]" : ""} ${
              isMobileOnly ? "leading-[23px] h-[23px]" : "leading-[27px]"
            } text-xs font-sub-section-header-font font-bold text-text-functional`}
          >
            {title}
          </span>
          <div className={className}>
            {Object.keys(comparisonTableCell).map((cellKey) => {
              const { text, icon, coverType } = comparisonTableCell[cellKey];

              const dynamicCoverLimit =
                attributes?.priceResult?.dynamicCoverLimit?.[0]?.product?.find(
                  (coverPackage: DynamicCoverLimitProduct) =>
                    coverPackage?.policyType ===
                      attributes.tripDetails?.policyType &&
                    coverPackage?.policyPackage === coverType?.toUpperCase()
                ) ?? null;

              const twoColumnCells: Record<
                TwoColumnCoverType,
                React.FC<ComparisionTableCellProps>
              > = {
                classic: TwoColumnLeftCell,
                deluxe: TwoColumnRightCell,
                basic: TwoColumnLeftCell,
                comprehensive: TwoColumnRightCell,
              };

              const coverCells: Record<
                CoverType,
                React.FC<ComparisionTableCellProps>
              > = {
                basic: BasicCell,
                comprehensive: ComprehensiveCell,
                signature: SignatureCell,
                essentials: BasicCell,
                classic: ComprehensiveCell,
                deluxe: SignatureCell,
              };
              const isSelected = dynamicCoverLimit?.values.find(
                (value: { selected: boolean }) => value.selected === true
              );

              const renderCancellationText = (data: BenefitCopyDataType) => {
                const { description, isCancelation } = data;
                if (isCancelation) {
                  if (
                    coverType.toUpperCase() === COMPREHENSIVE &&
                    !(brand === BRAND_STAYSURE)
                  ) {
                    return `${
                      (current.brandSpecificParams?.currencySymbol as string) ??
                      ""
                    }${formatToK("5000")}`;
                  }

                  if (isSelected) {
                    return `${
                      (current.brandSpecificParams?.currencySymbol as string) ??
                      ""
                    }${formatToK(isSelected.displayValue)}`;
                  }
                  return dynamicCoverLimit?.values[0]
                    ? `${
                        (current.brandSpecificParams
                          ?.currencySymbol as string) ?? ""
                      }${formatToK(dynamicCoverLimit.values[0].displayValue)}`
                    : description;
                }
                return description;
              };

              const getCoverCell = (cover: CoverType, key: string) => {
                const commonProps = {
                  type: icon,
                  label: renderCancellationText({
                    description: text,
                    isCancelation:
                      title === "Cancellation/cutting your trip short",
                  }),
                  // label: text,
                  cover,
                };
                if (isMobileOnly) {
                  return (
                    <MobileComparisonCell
                      brand={brand}
                      dynamicCoverLimit={dynamicCoverLimit}
                      setDynamicCoverLimit={setDynamicCoverLimit}
                      setCancelltionChangeCoverType={
                        setCancelltionChangeCoverType
                      }
                      setCancellationCoverOpen={setCancellationCoverOpen}
                      policyPackage={cover}
                      key={key}
                      {...commonProps}
                      isCancellation={
                        title === "Cancellation/cutting your trip short"
                      }
                    />
                  );
                }

                if (Object.keys(comparisonTableCell).length === 2) {
                  const Component = twoColumnCells[cover as TwoColumnCoverType];
                  return Component ? (
                    <Component key={key} {...commonProps} />
                  ) : null;
                }

                const Component = coverCells[cover];
                return Component ? (
                  <Component key={key} {...commonProps} />
                ) : null;
              };
              return <>{getCoverCell(coverType, cellKey)}</>;
            })}
          </div>
        </div>
      );
    });
  };

  return <>{renderTableBlocks(data)}</>;
}

export default withBrand(ComparisionTableBlock);
